// The item window's Pricing table, laid out like commonsku (Stephanie
// 2026-10-14: "make it more like commonsku's"): one table — Color/Size, Qty,
// Net cost, Margin, Retail (with the lock), Total — and the item's added
// costs as more rows in the same table (passed in as `children`). No repeated
// photo / name / description, no tax column, no charge bands: decoration
// charges live under their location, and the item summary holds the totals.
//
// Money: no new math. Row totals and margins use the shared module; a typed
// margin prices the row with sellPriceFromCostMills; Retail unlocked keeps the
// margin on a cost change with keepMarginSellPrice. Writes go through the same
// onPatchLine / onPatchVariant mutations the quote page uses.

import { Lock, LockOpen } from "lucide-react";
import {
  EditableCell,
  isValidCost,
  isValidCostOrBlank,
  isValidMargin,
  isValidMoney,
  isValidMoneyOrBlank,
  isValidQuantity,
} from "./DocumentLineCard";
import {
  costInputValue,
  formatCents,
  keepMarginSellPrice,
  marginHundredths,
  normalizeMoneyInput,
  normalizeQuantityInput,
  sellPriceFromCostMills,
  toCents,
  toMills,
} from "./money";
import { docLineTitle, docVariantLabel, type DocLine } from "./types";
import { cn } from "@/lib/utils";

/** One grid for product rows AND added-cost rows, so they line up:
 *  [remove] [name] [qty] [net cost] [margin] [retail] [total]. */
export const PRICING_COLS =
  "grid grid-cols-[28px_minmax(0,1fr)_110px_120px_100px_120px_130px] items-center gap-3";

/** Margin of one row, as a 2dp string for its box ("" when not computable). */
function rowMargin(priceCents: number, costMills: number): string {
  if (priceCents <= 0 || costMills <= 0) return "";
  return (((priceCents * 10 - costMills) / (priceCents * 10)) * 100).toFixed(2);
}

export function ItemPricingTable(props: {
  line: DocLine;
  currency: string;
  retailLocked: boolean;
  onToggleLock: () => void;
  onPatchLine: (body: Record<string, unknown>) => void;
  onPatchVariant: (variantId: string, body: Record<string, unknown>) => void;
  /** Added-cost rows, rendered in this table under the product rows. */
  children?: React.ReactNode;
}) {
  const { line, currency, retailLocked, onToggleLock, onPatchLine, onPatchVariant, children } = props;
  const linePriceCents = toCents(line.unitPrice);
  const lineCostMills = toMills(line.netCost ?? 0);

  const head = "text-xs font-bold uppercase tracking-wide text-brown-300";
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[820px]">
        <div className={cn(PRICING_COLS, "border-b border-brown-100 bg-cream-50 px-1 py-2", head)}>
          <span />
          <span>Color / Size</span>
          <span className="text-right">Qty</span>
          <span className="text-right">Net cost</span>
          <span className="text-right">Margin</span>
          <span className="flex items-center justify-end gap-1">
            Retail
            <button
              type="button"
              onClick={onToggleLock}
              aria-pressed={retailLocked}
              aria-label={retailLocked ? "Unlock retail" : "Lock retail"}
              title={
                retailLocked
                  ? "Retail locked: changing a cost keeps the price, the margin moves. Click to unlock."
                  : "Retail unlocked: changing a cost keeps the margin and re-prices. Click to lock."
              }
              className={retailLocked ? "text-rust-500" : "text-brown-300 hover:text-brown-500"}
            >
              {retailLocked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
            </button>
          </span>
          <span className="text-right">Total</span>
        </div>

        {line.variants.length > 0 ? (
          line.variants.map((v) => {
            const label = docVariantLabel(v);
            const priced = v.unitPrice != null && v.unitPrice !== "";
            const priceCents = priced ? toCents(v.unitPrice) : linePriceCents;
            const costed = v.unitCost != null && v.unitCost !== "";
            const costMills = costed ? toMills(v.unitCost) : lineCostMills;
            return (
              <div key={v.id} className={cn(PRICING_COLS, "border-b border-brown-50 px-1 py-2 text-[15px] tabular-nums")}>
                <span />
                <span className="truncate text-brown-500">{label}</span>
                <EditableCell
                  value={String(v.quantity)}
                  ariaLabel={`Quantity for ${label}`}
                  validate={isValidQuantity}
                  onCommit={(next) => onPatchVariant(v.id, { quantity: Number(normalizeQuantityInput(next)) })}
                />
                <EditableCell
                  value={costed ? costInputValue(v.unitCost) : costInputValue(line.netCost)}
                  prefix="$"
                  ariaLabel={`Cost for ${label}`}
                  validate={isValidCostOrBlank}
                  onCommit={(next) => {
                    const c = normalizeMoneyInput(next);
                    const body: Record<string, unknown> = { unit_cost: c === "" ? null : c };
                    if (!retailLocked) {
                      const sell = keepMarginSellPrice(costMills, priceCents, c === "" ? lineCostMills : toMills(c));
                      if (sell != null) body.unit_price = sell;
                    }
                    onPatchVariant(v.id, body);
                  }}
                />
                <EditableCell
                  value={rowMargin(priceCents, costMills)}
                  ariaLabel={`Margin for ${label}`}
                  validate={(m) => isValidMargin(m) && costMills > 0}
                  onCommit={(m) => {
                    const h = marginHundredths(m.trim().replace(/%$/, ""));
                    const sell = h == null ? null : sellPriceFromCostMills(costMills, h);
                    if (sell != null) onPatchVariant(v.id, { unit_price: sell });
                  }}
                />
                <EditableCell
                  value={priced ? String(v.unitPrice) : line.unitPrice}
                  prefix="$"
                  ariaLabel={`Retail for ${label}`}
                  validate={isValidMoneyOrBlank}
                  onCommit={(next) => {
                    const p = normalizeMoneyInput(next);
                    onPatchVariant(v.id, { unit_price: p === "" ? null : p });
                  }}
                />
                <span className="text-right font-semibold text-brown-500">
                  {formatCents(priceCents * v.quantity, currency)}
                </span>
              </div>
            );
          })
        ) : (
          <div className={cn(PRICING_COLS, "border-b border-brown-50 px-1 py-2 text-[15px] tabular-nums")}>
            <span />
            <span className="truncate text-brown-500">{docLineTitle(line)}</span>
            <EditableCell
              value={String(line.quantityTotal)}
              ariaLabel="Quantity"
              validate={isValidQuantity}
              onCommit={(next) => onPatchLine({ quantity_total: Number(normalizeQuantityInput(next)) })}
            />
            <EditableCell
              value={costInputValue(line.netCost)}
              prefix="$"
              ariaLabel="Net cost"
              validate={isValidCost}
              onCommit={(next) => {
                const c = normalizeMoneyInput(next);
                const body: Record<string, unknown> = { net_cost: c };
                if (!retailLocked) {
                  const sell = keepMarginSellPrice(lineCostMills, linePriceCents, toMills(c));
                  if (sell != null) body.unit_price = sell;
                }
                onPatchLine(body);
              }}
            />
            <EditableCell
              value={rowMargin(linePriceCents, lineCostMills)}
              ariaLabel="Margin"
              validate={(m) => isValidMargin(m) && lineCostMills > 0}
              onCommit={(m) => {
                const h = marginHundredths(m.trim().replace(/%$/, ""));
                const sell = h == null ? null : sellPriceFromCostMills(lineCostMills, h);
                if (sell != null) onPatchLine({ unit_price: sell });
              }}
            />
            <EditableCell
              value={line.unitPrice}
              prefix="$"
              ariaLabel="Retail"
              validate={isValidMoney}
              onCommit={(next) => onPatchLine({ unit_price: normalizeMoneyInput(next) })}
            />
            <span className="text-right font-semibold text-brown-500">
              {formatCents(linePriceCents * line.quantityTotal, currency)}
            </span>
          </div>
        )}

        {children}
      </div>
    </div>
  );
}
